import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ShieldCheck, Clock3, Gauge, Lightbulb } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { PRICING_COPY } from "@/data/pricingCopy";

const SITE = "https://oceandata.ca";
const CONTRAST_ICONS = [Clock3, Gauge, ShieldCheck];

export default function PricingPage({ lang = "en" }) {
  const { setOpen } = useSimplify();
  const t = PRICING_COPY[lang] || PRICING_COPY.en;

  useEffect(() => {
    document.title = t.docTitle;
    document.documentElement.lang = lang;
    const links = [];
    Object.entries(t.hreflangs).forEach(([hreflang, path]) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = `${SITE}${path}`;
      el.setAttribute("data-hreflang-injected", "true");
      document.head.appendChild(el);
      links.push(el);
    });
    return () => {
      document.documentElement.lang = "en";
      links.forEach((el) => el.remove());
    };
  }, [t, lang]);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: t.schemaName,
        inLanguage: t.schemaLang,
        provider: { "@type": "Organization", name: "Ocean Data", url: SITE },
        description: t.schemaDesc,
        offers: [
          { "@type": "Offer", name: t.tiers[0].name, price: "899", priceCurrency: "USD", description: t.tiers[0].headline },
          { "@type": "Offer", name: t.tiers[1].name, price: "1799", priceCurrency: "USD", description: t.tiers[1].headline },
          { "@type": "Offer", name: t.tiers[2].name, price: "3699", priceCurrency: "USD", description: t.tiers[2].headline },
          { "@type": "Offer", name: t.tiers[3].name, price: "4779", priceCurrency: "USD", description: t.tiers[3].headline },
        ],
      },
      {
        "@type": "FAQPage",
        inLanguage: t.schemaLang,
        mainEntity: t.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang={lang}>
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            {t.eyebrow}
          </p>
          <h1
            data-testid="pricing-headline"
            className="font-display font-bold tracking-tight leading-[1.04] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="pricing-headline-line-1">{t.h1a}</MaskedLine>
            <MaskedLine delay={0.3} testId="pricing-headline-line-2">
              <span className="text-brand-teal">{t.h1b}</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            {t.heroSub}
          </motion.p>
        </div>
      </section>

      {/* Free-trial band */}
      <section className="pb-14 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div
              data-testid="pricing-trial-band"
              className="rounded-3xl bg-brand-navy text-white px-7 sm:px-12 py-10 sm:py-12 grid grid-cols-12 gap-8 items-center"
            >
              <div className="col-span-12 lg:col-span-8">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand-teal font-semibold mb-3">
                  {t.trialEyebrow}
                </p>
                <h2 className="font-display font-semibold text-2xl sm:text-3xl leading-snug mb-3">
                  {t.trialTitle}
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl">
                  {t.trialBody}
                </p>
              </div>
              <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
                <button
                  data-testid="pricing-trial-cta"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
                >
                  {t.trialCta}
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Tiers */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-7 items-stretch">
          {t.tiers.map((tier, i) => (
            <Reveal key={tier.id} delay={i * 0.08} className="h-full">
              <div
                data-testid={tier.id}
                className={`relative h-full rounded-3xl p-8 sm:p-9 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                  tier.featured
                    ? "bg-brand-navy text-white shadow-[0_30px_60px_-20px_rgba(14,20,51,0.45)]"
                    : "bg-white border border-slate-200 hover:border-brand-teal"
                }`}
              >
                {tier.featured && (
                  <span
                    data-testid="pricing-recommended-badge"
                    className="absolute -top-3.5 left-8 rounded-full bg-brand-gold text-brand-navy font-mono text-[10px] uppercase tracking-[0.16em] font-semibold px-4 py-1.5"
                  >
                    {t.mostChosen}
                  </span>
                )}
                <p className="font-mono text-xs uppercase tracking-[0.2em] font-semibold mb-2 text-brand-teal">
                  {tier.name}
                </p>
                <p className={`font-display font-bold text-3xl sm:text-4xl tracking-tight mb-1 ${tier.featured ? "text-white" : "text-brand-navy"}`}>
                  {tier.price}
                </p>
                <p className="text-xs mb-6 text-slate-400">
                  {tier.priceNote}
                </p>
                <h3 className={`font-display font-semibold text-xl leading-snug mb-3 ${tier.featured ? "text-white" : "text-brand-navy"}`}>
                  {tier.headline}
                </h3>
                <p className={`text-sm leading-relaxed mb-7 ${tier.featured ? "text-slate-300" : "text-slate-600"}`}>
                  {tier.body}
                </p>
                <ul className="space-y-3 mb-9 flex-1">
                  {tier.includes.map((inc) => (
                    <li key={inc} className={`flex items-start gap-2.5 text-sm ${tier.featured ? "text-slate-200" : "text-slate-700"}`}>
                      <Check size={15} className="text-brand-teal mt-0.5 shrink-0" strokeWidth={3} />
                      {inc}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`${tier.id}-cta`}
                  onClick={() => setOpen(true)}
                  className={`w-full inline-flex items-center justify-center gap-2.5 rounded-full text-sm font-semibold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 ${
                    tier.featured
                      ? "bg-brand-teal text-white hover:bg-brand-tealhover"
                      : "bg-brand-navy text-white hover:bg-brand-teal"
                  }`}
                >
                  {tier.cta}
                  <ArrowRight size={16} />
                </button>
                <p className="mt-3 text-center text-[11px] text-slate-400">
                  {t.underCta}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Contrast strip */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl border border-slate-200 bg-white px-7 sm:px-12 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {t.contrast.map((s, i) => {
                const Icon = CONTRAST_ICONS[i];
                return (
                  <div key={i} className="flex items-start gap-4">
                    <span className="w-11 h-11 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center shrink-0">
                      <Icon size={18} />
                    </span>
                    <div>
                      <p className="font-display font-bold text-2xl text-brand-navy">{s.stat}</p>
                      <p className="text-sm text-slate-600 leading-relaxed mt-1">{s.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-xs text-slate-400 max-w-xl">
              {t.contrastFoot}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-12 gap-8 lg:gap-12">
          <Reveal className="col-span-12 lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
              {t.faqEyebrow}
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl mb-5">
              {t.faqTitle}
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              {t.faqBody}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-7 lg:col-start-6">
            <Accordion type="single" collapsible data-testid="pricing-faq-accordion" className="w-full">
              {t.faqs.map((f, i) => (
                <AccordionItem key={i} value={`pfaq-${i}`} data-testid={`pricing-faq-item-${i}`} className="border-b border-slate-200">
                  <AccordionTrigger
                    data-testid={`pricing-faq-question-${i}`}
                    className="text-left font-display font-medium text-base sm:text-lg text-brand-navy hover:text-brand-teal py-6 transition-colors"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent data-testid={`pricing-faq-answer-${i}`} className="text-sm sm:text-base text-slate-600 leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Made to Stick */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div data-testid="pricing-stick-panel" className="rounded-3xl bg-brand-tealsoft border border-brand-tealborder p-8 sm:p-10">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-teal font-semibold mb-6">
                <Lightbulb size={14} /> {t.stickTitle}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {t.stick.map(([label, value], i) => (
                  <div key={i} className="rounded-xl bg-white/70 border border-brand-tealborder/60 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-2">{label}</p>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-brand-navy text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <Reveal>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-2xl sm:text-3xl lg:text-4xl max-w-2xl mx-auto mb-5">
              {t.closeTitle}
            </h2>
            <p className="text-base text-slate-300 leading-relaxed max-w-xl mx-auto mb-9">
              {t.closeBody}
            </p>
            <button
              data-testid="pricing-bottom-cta"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-10 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
            >
              {t.closeCta}
              <ArrowRight size={18} />
            </button>
            <p className="mt-8 text-sm text-slate-400">
              {t.seeJourney}{" "}
              <Link to={t.seeJourneyTo} className="font-semibold text-brand-gold hover:text-white transition-colors">
                {t.seeJourneyLink}
              </Link>
              {"  ·  "}
              <Link to="/demos" className="font-semibold text-brand-gold hover:text-white transition-colors">
                {t.demosLink}
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
